import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, BuildingOffice2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import BranchCard from '../components/BranchCard';
import BranchModal from '../components/BranchModal';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminBranchManagementPage = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, branchId: null });
    const [searchQuery, setSearchQuery] = useState('');

    const fetchBranches = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/branches');
            if (response.data.success) {
                setBranches(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching branches:', error);
            toast.error('Failed to load branches');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingBranch) {
                await axios.put(`/api/admin/branches/${editingBranch.id}`, formData);
                toast.success('Branch updated successfully');
            } else {
                await axios.post('/api/admin/branches', formData);
                toast.success('Branch created successfully');
            }
            setIsModalOpen(false);
            setEditingBranch(null);
            fetchBranches();
        } catch (error) {
            console.error('Error saving branch:', error);
            toast.error(error.response?.data?.message || 'Failed to save branch');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, branchId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/branches/${deleteConfirm.branchId}`);
            toast.success('Branch deleted successfully');
            setDeleteConfirm({ isOpen: false, branchId: null });
            fetchBranches();
        } catch (error) {
            console.error('Error deleting branch:', error);
            toast.error('Failed to delete branch');
        }
    };

    const handleStatusToggle = async (id, newStatus) => {
        try {
            await axios.patch(`/api/admin/branches/${id}/status`, { is_active: newStatus });
            toast.success(`Branch ${newStatus ? 'activated' : 'deactivated'}`);
            setBranches(branches.map(b => b.id === id ? { ...b, is_active: newStatus } : b));
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    const filteredBranches = branches.filter(branch =>
        branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        branch.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        branch.state.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Branch Management</h1>
                    <p className="text-slate-500 mt-1">Manage 26+ office locations across the network</p>
                </div>
                <button
                    onClick={() => {
                        setEditingBranch(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3.5 rounded-2xl font-bold transition-all shadow-lg shadow-primary-100 hover:shadow-xl hover:-translate-y-0.5"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Branch
                </button>
            </div>

            {/* Filters & Search */}
            <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by name, city or state..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                    />
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <div className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-500 text-sm font-bold whitespace-nowrap">
                        Total: {branches.length} Branches
                    </div>
                </div>
            </div>

            {/* Grid Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                        <div key={i} className="bg-white rounded-3xl h-64 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : filteredBranches.length === 0 ? (
                <div className="bg-white rounded-[2.5rem] p-16 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <BuildingOffice2Icon className="h-10 w-10 text-slate-300" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-3">No branches found</h3>
                        <p className="text-slate-500 mb-8 leading-relaxed">
                            {searchQuery ? `No branches match your search "${searchQuery}".` : "You haven't added any branches to the system yet."}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-primary-100"
                            >
                                Add Your First Branch
                            </button>
                        )}
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="text-primary-600 font-bold hover:underline"
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredBranches.map((branch) => (
                        <BranchCard
                            key={branch.id}
                            branch={branch}
                            onEdit={(b) => {
                                setEditingBranch(b);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                            onStatusToggle={handleStatusToggle}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <BranchModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingBranch(null);
                }}
                onSubmit={handleSubmit}
                branch={editingBranch}
                loading={submitting}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, branchId: null })}
                onConfirm={confirmDelete}
                title="Delete Branch"
                message="Are you sure you want to delete this branch? This will permanently remove the location from the locator."
            />
        </div>
    );
};

export default AdminBranchManagementPage;
